import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { Referral } from '@/hooks/useReferrals';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { ReferralReceiptBadge } from '@/components/patient/ReferralReceiptBadge';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import {
  BOOKING_TEXT_MAX,
  CONTACT_FIELD_MAX,
  DEFAULT_CONTACT_TITLE,
  REPLY_SUBJECT_MAX,
  REPLY_TEXT_MAX,
  bookingContactPayload,
  bookingIssues,
  bookingPhrases,
  contactIssues,
  correspondenceReplyIssues,
  readSentBooking,
  replyEpisodeIdentifier,
  replyReceiverRule,
  type ReplyKind,
} from '@/lib/referral/referralReply';
import { readPostalAddress } from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

// Spor A REF01-M 3.31–3.39: answer a received XREF01 with an XDIS13 bookingsvar
// or an XDIS91 korrespondance. Communicate builds it from the received XML and
// carries over the referrer's UUID (EpisodeOfCareIdentifier) and the receiver
// (Referrer/Identifier, else Sender/Identifier). The clinic is the sender.
// DIS13-S 2.4–2.7, 3.8 (T1.5): the booking text is a fixed paraphrase, the
// kontaktperson goes in Sender/Contact, and "Ret bookingsvar" sends a rettetbrev
// (new envelope/letter id, same forløbsnummer) that corrects a sent bookingsvar.

const SECTION =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card  ';
const HEADING = 'text-xs font-medium uppercase tracking-wide text-ds-muted ';
const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';

type ReplyRow = {
  id: string;
  kind: ReplyKind;
  exchange_id: string;
  envelope_identifier: string;
  receiver_organisation_name: string | null;
  receiver_ean: string;
  status: string;
  created_at: string;
  letter_status_code: 'nytbrev' | 'rettetbrev' | null;
  corrects_id: string | null;
};

type Check = { id: string; pass: boolean; detail: string };
type ReplyResult = { id: string; envelopeIdentifier: string; ok: boolean; kind: ReplyKind; checks: Check[] };

type ReplyRequest = {
  action: 'reply';
  clinicId: string;
  referralId: string;
  kind: ReplyKind;
  sender: Record<string, string>;
  confirmation?: { date: string; time?: string };
  text?: string;
  statusCode?: 'nytbrev' | 'rettetbrev';
  correctsReplyId?: string;
  contact?: { title?: string; name?: string };
  correspondence?: { priority?: 'rutine' | 'høj_prioritet'; subject?: string; text?: string };
};

function filled(entries: Record<string, string | null | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

function useReferralReplies(referralId: string) {
  return useQuery({
    queryKey: ['referral-replies', referralId],
    queryFn: async (): Promise<ReplyRow[]> => {
      const { data, error } = await supabase
        .from('clinic_communicate_outbound_referrals')
        .select(
          'id, kind, exchange_id, envelope_identifier, receiver_organisation_name, receiver_ean, status, created_at, letter_status_code, corrects_id',
        )
        .eq('source_referral_id', referralId)
        .in('kind', ['XDIS13', 'XDIS91'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ReplyRow[];
    },
  });
}

export function ReferralReplyPanel({ referral }: { referral: Referral }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const clinicQuery = useClinic(referral.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(referral.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const replies = useReferralReplies(referral.id);
  const [open, setOpen] = useState<ReplyKind | null>(null);
  const [booking, setBooking] = useState({ date: '', time: '', text: '' });
  const [contact, setContact] = useState({ title: DEFAULT_CONTACT_TITLE, name: '' });
  /** The sent bookingsvar being corrected ("Ret bookingsvar"), else null for a new one. */
  const [correcting, setCorrecting] = useState<ReplyRow | null>(null);
  const [corr, setCorr] = useState<{ priority: 'rutine' | 'høj_prioritet'; subject: string; text: string }>({
    priority: 'rutine',
    subject: '',
    text: '',
  });
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReplyResult | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const details = referral.medcom_details;
  const episode = replyEpisodeIdentifier(details);
  const receiverRule = replyReceiverRule(details);
  const medicalSpecialityCode = readClinicMedicalSpecialityCode(connectionQuery.data?.medical_speciality_code);
  const phrases = bookingPhrases(clinicQuery.data?.name ?? '');
  const issues =
    open === 'XDIS13'
      ? [...bookingIssues({ ...booking, phrases }), ...contactIssues(contact)]
      : open === 'XDIS91'
        ? correspondenceReplyIssues(corr)
        : [];

  const senderGap = !connectionQuery.data?.ean
    ? t('referrals.revisit_sender_missing_ean', 'Klinikken har ingen MedCom-forbindelse med lokationsnummer.')
    : !senderSor.data?.identifier || !senderSor.data.identifierCode
      ? t('referrals.revisit_sender_missing_sor', 'Klinikkens SOR-enhed mangler identifikator.')
      : !medicalSpecialityCode
        ? t('referrals.revisit_sender_missing_speciality', 'Klinikkens speciale (MedicalSpecialityCode) er ikke konfigureret.')
        : null;

  function openForm(kind: ReplyKind) {
    setOpen(kind);
    setCorrecting(null);
    setAttempted(false);
    setError(null);
  }

  function closeForm() {
    setOpen(null);
    setCorrecting(null);
    setBooking({ date: '', time: '', text: '' });
    setContact({ title: DEFAULT_CONTACT_TITLE, name: '' });
  }

  async function onCorrect(row: ReplyRow) {
    setBusyId(row.id);
    try {
      const payload = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string },
        { xml?: string }
      >('communicate-ref', { action: 'payload', clinicId: referral.clinic_id, exchangeId: row.exchange_id });
      if (payload.xml === undefined) throw new Error('Missing XML payload');
      const sent = readSentBooking(payload.xml);
      setBooking({ date: sent.date, time: sent.time, text: phrases.includes(sent.text) ? sent.text : '' });
      setContact({ title: sent.contact.title || DEFAULT_CONTACT_TITLE, name: sent.contact.name });
      setCorrecting(row);
      setResult(null);
      setOpen('XDIS13');
      setAttempted(false);
      setError(null);
    } catch (err) {
      toast.error(
        err instanceof ApiCallError
          ? err.message
          : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onDownload(row: ReplyRow) {
    setBusyId(row.id);
    try {
      const payload = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string },
        { xml?: string }
      >('communicate-ref', { action: 'payload', clinicId: referral.clinic_id, exchangeId: row.exchange_id });
      if (payload.xml === undefined) throw new Error('Missing XML payload');
      downloadLatin1Xml(payload.xml, `${row.kind}_${row.envelope_identifier}.xml`);
    } catch (err) {
      toast.error(
        err instanceof ApiCallError
          ? err.message
          : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onSubmit() {
    if (!open) return;
    setAttempted(true);
    setError(null);
    // Never block silently: every refusal shows a toast as well as the field message.
    if (senderGap) {
      toast.error(senderGap);
      return;
    }
    if (issues.length > 0) {
      toast.error(t('referrals.reply_invalid', 'Svaret er ikke dannet. Ret de markerede felter.'));
      return;
    }
    const clinic = clinicQuery.data;
    const sor = senderSor.data;
    const connection = connectionQuery.data;
    if (!clinic || !sor?.identifier || !sor.identifierCode || !connection?.ean || !medicalSpecialityCode) {
      toast.error(t('referrals.reply_not_ready', 'Klinikkens MedCom-oplysninger er ikke indlæst endnu. Prøv igen om lidt.'));
      return;
    }
    const contactPayload = bookingContactPayload(contact);
    const clinicAddress = readPostalAddress(clinic.address);
    const body: ReplyRequest = {
      action: 'reply',
      clinicId: referral.clinic_id,
      referralId: referral.id,
      kind: open,
      sender: filled({
        ean: connection.ean,
        identifier: sor.identifier,
        identifierCode: sor.identifierCode,
        organisationName: clinic.name,
        streetName: clinicAddress.street,
        postCodeIdentifier: clinicAddress.postCode,
        districtName: clinicAddress.district,
        telephoneSubscriberIdentifier: clinic.phone,
        medicalSpecialityCode,
      }),
      ...(open === 'XDIS13'
        ? {
            confirmation: booking.time ? { date: booking.date, time: booking.time } : { date: booking.date },
            ...(booking.text.trim() ? { text: booking.text.trim() } : {}),
            statusCode: correcting ? ('rettetbrev' as const) : ('nytbrev' as const),
            ...(correcting ? { correctsReplyId: correcting.id } : {}),
            ...(contactPayload ? { contact: contactPayload } : {}),
          }
        : {
            correspondence: {
              priority: corr.priority,
              ...(corr.subject.trim() ? { subject: corr.subject.trim() } : {}),
              ...(corr.text.trim() ? { text: corr.text } : {}),
            },
          }),
    };
    setPending(true);
    try {
      const built = await callFunction<ReplyRequest, ReplyResult>('communicate-ref', body);
      setResult(built);
      await queryClient.invalidateQueries({ queryKey: ['referral-replies', referral.id] });
      if (referral.patient_id) {
        await queryClient.invalidateQueries({
          queryKey: ['hospital-referral-outbox', referral.clinic_id, referral.patient_id],
        });
      }
      toast.success(
        open === 'XDIS13' && correcting
          ? t('referrals.reply_booking_corrected', 'Det rettede bookingsvar er dannet og ligger i udbakken.')
          : open === 'XDIS13'
          ? t('referrals.reply_booking_built', 'Bookingsvaret er dannet og ligger i udbakken.')
          : t('referrals.reply_correspondence_built', 'Korrespondancen er dannet og ligger i udbakken.'),
      );
      closeForm();
      setCorr({ priority: 'rutine', subject: '', text: '' });
      setAttempted(false);
    } catch (err) {
      const message = err instanceof ApiCallError ? err.message : t('referrals.reply_failed', 'Svaret kunne ikke dannes.');
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={SECTION} aria-labelledby="referral-reply-heading" data-testid="referral-reply-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ds-line px-4 py-2.5">
        <span id="referral-reply-heading" className={HEADING}>
          {t('referrals.reply_title', 'Svar på henvisningen')}
        </span>
        {!open && (
          <span className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => openForm('XDIS13')} disabled={!referral.patient_id}>
              {t('referrals.reply_booking_open', 'Send bookingsvar (XDIS13)')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => openForm('XDIS91')}
              disabled={!referral.patient_id}
            >
              {t('referrals.reply_correspondence_open', 'Send korrespondance (XDIS91)')}
            </Button>
          </span>
        )}
      </div>

      {!referral.patient_id && (
        <p className={cn('px-4 py-3', FORM_HINT_TYPE)}>
          {t('referrals.reply_needs_patient', 'Knyt henvisningen til en patient, før der svares.')}
        </p>
      )}

      {open && (
        <div className="space-y-4 px-4 py-4">
          {open === 'XDIS13' && correcting && (
            <div
              role="status"
              data-testid="reply-correcting-banner"
              className="rounded-md border border-ds-amber-line bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink"
            >
              <p className="font-medium">
                {t('referrals.reply_correcting', 'Du retter et tidligere fremsendt bookingsvar (kuvert {{envelope}}, {{date}}).', {
                  envelope: correcting.envelope_identifier,
                  date: formatReferralDateTime(correcting.created_at),
                })}
              </p>
              <p className="mt-0.5">
                {t(
                  'referrals.reply_correcting_hint',
                  'Det sendes som et rettet bookingsvar (StatusCode rettetbrev) med nyt kuvert- og brev-id og samme forløbsnummer.',
                )}
              </p>
            </div>
          )}
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className={HEADING}>{t('referrals.reply_episode', 'Forløbsnummer (EpisodeOfCareIdentifier)')}</dt>
              <dd className="mt-1 break-all font-mono text-ds-text" data-testid="reply-episode">
                {episode.value}
              </dd>
              <dd className={cn('mt-0.5', FORM_HINT_TYPE)}>
                {episode.fromReferrer
                  ? t('referrals.reply_episode_from_referrer', 'Henviserens UUID fra henvisningen, uden bindestreger.')
                  : t('referrals.reply_episode_unknown', 'Henvisningen har intet henviser-UUID, derfor 1.')}
              </dd>
            </div>
            <div>
              <dt className={HEADING}>{t('referrals.reply_receiver', 'Modtager')}</dt>
              <dd className="mt-1 text-ds-text" data-testid="reply-receiver">
                {receiverRule.rule === 'referrer'
                  ? t('referrals.reply_receiver_referrer', 'Henviser {{identifier}} ({{code}})', {
                      identifier: receiverRule.identifier,
                      code: receiverRule.identifierCode ?? '—',
                    })
                  : t('referrals.reply_receiver_sender', 'Henvisningens afsender (Sender/Identifier)')}
              </dd>
              <dd className={cn('mt-0.5', FORM_HINT_TYPE)}>
                {t('referrals.reply_receiver_rule', 'Referrer/Identifier hvis udfyldt, ellers Sender/Identifier (XREF01).')}
              </dd>
            </div>
          </dl>

          {senderGap && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {senderGap}
            </p>
          )}

          {open === 'XDIS13' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="reply-booking-date" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('referrals.reply_booking_date', 'Indkaldelsesdato')}
                </label>
                <input
                  id="reply-booking-date"
                  type="date"
                  value={booking.date}
                  onChange={(e) => setBooking((b) => ({ ...b, date: e.target.value }))}
                  aria-invalid={attempted && issues.includes('date')}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              <div>
                <label htmlFor="reply-booking-time" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('referrals.reply_booking_time', 'Tidspunkt (valgfrit)')}
                </label>
                <input
                  id="reply-booking-time"
                  type="time"
                  value={booking.time}
                  onChange={(e) => setBooking((b) => ({ ...b, time: e.target.value }))}
                  aria-invalid={attempted && issues.includes('time')}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="reply-booking-text" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('referrals.reply_booking_text', 'Indkaldelsestekst')}
                </label>
                <select
                  id="reply-booking-text"
                  value={booking.text}
                  onChange={(e) => setBooking((b) => ({ ...b, text: e.target.value }))}
                  aria-invalid={attempted && issues.includes('text_missing')}
                  className={cn(TYPE.inputValue, FIELD)}
                >
                  <option value="">{t('referrals.reply_booking_text_choose', 'Vælg en fast tekst …')}</option>
                  {phrases.map((phrase) => (
                    <option key={phrase} value={phrase}>
                      {phrase}
                    </option>
                  ))}
                </select>
                <p className={cn('mt-1', FORM_HINT_TYPE)}>
                  {t(
                    'referrals.reply_booking_text_fixed_hint',
                    'Faste tekster (højst {{max}} tegn, XDIS13 Text03). Dato og tid sendes i indkaldelsesfeltet, ikke i teksten.',
                    { max: BOOKING_TEXT_MAX },
                  )}
                </p>
              </div>
              <div>
                <label htmlFor="reply-contact-title" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('referrals.reply_contact_title', 'Kontaktperson, titel')}
                </label>
                <input
                  id="reply-contact-title"
                  type="text"
                  maxLength={CONTACT_FIELD_MAX}
                  value={contact.title}
                  onChange={(e) => setContact((c) => ({ ...c, title: e.target.value }))}
                  aria-invalid={attempted && issues.includes('contact_too_long') && contact.title.trim().length > CONTACT_FIELD_MAX}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              <div>
                <label htmlFor="reply-contact-name" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('referrals.reply_contact_name', 'Kontaktperson, navn (valgfrit)')}
                </label>
                <input
                  id="reply-contact-name"
                  type="text"
                  maxLength={CONTACT_FIELD_MAX}
                  value={contact.name}
                  onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                  aria-invalid={attempted && issues.includes('contact_too_long') && contact.name.trim().length > CONTACT_FIELD_MAX}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              {attempted && issues.includes('text_missing') && (
                <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
                  {t('referrals.reply_booking_text_missing', 'Vælg en af de faste bookingtekster.')}
                </p>
              )}
              {attempted && issues.includes('contact_too_long') && (
                <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
                  {t('referrals.reply_contact_too_long', 'Kontaktpersonens titel og navn må højst være {{max}} tegn hver.', {
                    max: CONTACT_FIELD_MAX,
                  })}
                </p>
              )}
              {attempted && issues.includes('date') && (
                <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
                  {t('referrals.reply_booking_date_missing', 'Angiv indkaldelsesdato.')}
                </p>
              )}
              {attempted && issues.includes('time') && (
                <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
                  {t('referrals.reply_booking_time_invalid', 'Tidspunktet skal være TT:MM, eller lad feltet stå tomt.')}
                </p>
              )}
              {attempted && issues.includes('text_too_long') && (
                <p role="alert" className={cn('sm:col-span-2', FORM_ERROR_TYPE)}>
                  {t('referrals.reply_booking_text_too_long', 'Indkaldelsesteksten må højst være {{max}} tegn.', {
                    max: BOOKING_TEXT_MAX,
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label htmlFor="reply-corr-priority" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('patients.correspondence.priority', 'Prioritet')}
                </label>
                <select
                  id="reply-corr-priority"
                  value={corr.priority}
                  onChange={(e) =>
                    setCorr((c) => ({ ...c, priority: e.target.value === 'høj_prioritet' ? 'høj_prioritet' : 'rutine' }))
                  }
                  className={cn(TYPE.inputValue, FIELD)}
                >
                  <option value="rutine">{t('patients.correspondence.priority_rutine', 'Rutine')}</option>
                  <option value="høj_prioritet">{t('patients.correspondence.priority_high', 'Høj prioritet')}</option>
                </select>
              </div>
              <div>
                <label htmlFor="reply-corr-subject" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('patients.correspondence.subject', 'Emne')}
                </label>
                <input
                  id="reply-corr-subject"
                  type="text"
                  maxLength={REPLY_SUBJECT_MAX}
                  value={corr.subject}
                  onChange={(e) => setCorr((c) => ({ ...c, subject: e.target.value }))}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              <div>
                <label htmlFor="reply-corr-text" className={cn('block', FORM_LABEL_TYPE)}>
                  {t('patients.correspondence.text', 'Tekst')}
                </label>
                <textarea
                  id="reply-corr-text"
                  rows={5}
                  maxLength={REPLY_TEXT_MAX}
                  value={corr.text}
                  onChange={(e) => setCorr((c) => ({ ...c, text: e.target.value }))}
                  className={cn(TYPE.inputValue, FIELD)}
                />
              </div>
              {attempted && issues.includes('empty') && (
                <p role="alert" className={FORM_ERROR_TYPE}>
                  {t('referrals.reply_correspondence_empty', 'Skriv et emne eller en tekst.')}
                </p>
              )}
            </div>
          )}

          {error && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={() => void onSubmit()} disabled={pending || Boolean(senderGap)}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {open === 'XDIS13' && correcting
                ? t('referrals.reply_booking_correct_submit', 'Dan rettet bookingsvar')
                : open === 'XDIS13'
                ? t('referrals.reply_booking_submit', 'Dan bookingsvar')
                : t('referrals.reply_correspondence_submit', 'Dan korrespondance')}
            </Button>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={pending}>
              {t('common.cancel', 'Annuller')}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border-t border-ds-line px-4 py-3 text-sm" data-testid="reply-checks">
          <ul className="space-y-1">
            {result.checks.map((check) => (
              <li key={check.id} className={check.pass ? 'text-ds-green-ink ' : 'text-ds-red-ink'}>
                {check.pass ? '✓' : '✗'} {check.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(replies.data?.length ?? 0) > 0 && (
        <div className="border-t border-ds-line px-4 py-3">
          <div className={HEADING}>{t('referrals.reply_sent', 'Sendte svar')}</div>
          <ul className="mt-2 divide-y divide-ds-line">
            {replies.data?.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="text-ds-text">
                  {row.kind === 'XDIS13'
                    ? t('referrals.reply_kind_xdis13', 'Bookingsvar (XDIS13)')
                    : t('referrals.reply_kind_xdis91', 'Korrespondance (XDIS91)')}{' '}
                  {row.letter_status_code === 'rettetbrev' && (
                    <span
                      data-testid="reply-rettet-badge"
                      className="mr-1 rounded bg-ds-amber-tint px-1.5 py-0.5 text-xs font-medium text-ds-amber-ink"
                    >
                      {t('referrals.reply_rettet', 'Rettet')}
                    </span>
                  )}
                  · {row.receiver_organisation_name ?? row.receiver_ean} · {row.envelope_identifier} ·{' '}
                  {formatReferralDateTime(row.created_at)}
                </span>
                <span className="flex items-center gap-2">
                  <ReferralReceiptBadge status={row.status} />
                  {row.kind === 'XDIS13' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === row.id || pending || Boolean(senderGap)}
                      onClick={() => void onCorrect(row)}
                    >
                      {t('referrals.reply_booking_correct', 'Ret bookingsvar')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busyId === row.id}
                    onClick={() => void onDownload(row)}
                  >
                    {t('referrals.revisit_download', 'Hent XML')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
          <p className={cn('mt-1', FORM_HINT_TYPE)}>
            {t('referrals.revisit_receipts_hint', 'Kvitteringer og VANS håndteres i patientens udbakke.')}
          </p>
        </div>
      )}
    </section>
  );
}
